import { dateValueToMillis, type DateValue } from "../../types/data";
import { useEffect, useMemo, useState } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { Link, NavLink, useLocation } from "react-router-dom";
import {
  IoIosArrowBack,
  IoIosArrowForward,
  IoIosPerson,
  IoIosPeople,
  IoMdFilm,
  IoMdStarHalf,
  IoMdTrophy,
  IoMdAlbums
} from "react-icons/io";
import { FaBookOpen, FaChartPie, FaCompass, FaCrown } from "react-icons/fa";
import { preloadRoute } from "../../utility/routeLoaders";
import { useFirebase } from "../../context/FirebaseContext";
import tmdbShortLogo from "../../images/TMDB_short.svg";
import { useI18n } from "../../i18n/useI18n";
import { isDemoUserId } from "../../utility/demoSession";
import { useWeeklyLegendaryAvailability } from "../../hooks/useWeeklyLegendaryAvailability";
import { useInterfacePreferences } from "../../hooks/useInterfacePreferences";

interface NotificationBadgeState { lastViewedAt: number; recent: { read: boolean; createdAt: number }[]; }

function LeftPanel({ userId, isMobileMenuOpen, onCloseMobileMenu }: { userId?: string | null; isMobileMenuOpen?: boolean; onCloseMobileMenu?: () => void }) {
  const { t } = useI18n();
  const { auth, firestore } = useFirebase();
  const [user] = useAuthState(auth);
  const location = useLocation();
  const [isExpanded, setIsExpanded] = useState(true);
  const legendaryAvailability = useWeeklyLegendaryAvailability();
  const {
    legendaryHighlights,
    notificationBadges
  } = useInterfacePreferences();
  const [notificationState, setNotificationState] = useState<NotificationBadgeState>({
    lastViewedAt: 0,
    recent: []
  });
  const routeSection = useMemo(() => {
    const parts = location.pathname.split("/").filter(Boolean);
    return parts[1] || "profile";
  }, [location.pathname]);
  const activeSection = routeSection === "notifications" ? "activity" : routeSection;

  useEffect(() => {
    // Notifications are a real social channel. A demo session must not create
    // a listener (and therefore paid reads) for a notification feed it cannot
    // use, even if somebody guesses its temporary UID.
    if (!notificationBadges || !user?.uid || isDemoUserId(user.uid)) {
      setNotificationState({ lastViewedAt: 0, recent: [] });
      return undefined;
    }
    const ownerRef = firestore.collection("userNotifications").doc(user.uid);
    // Keep the listener bounded. The badge only needs to answer whether recent
    // events exist; subscribing to every unread document makes each reconnect
    // bill the entire notification history.
    const unsubscribeOwner = ownerRef.onSnapshot(
      (snapshot) => {
        const value: DateValue | undefined = snapshot.data()?.lastViewedAt;
        const lastViewedAt = dateValueToMillis(value) || 0;
        setNotificationState((current) => ({ ...current, lastViewedAt }));
      },
      () => setNotificationState((current) => ({ ...current, lastViewedAt: 0 }))
    );
    const unsubscribeRecent = ownerRef
      .collection("notifications")
      .where("read", "==", false)
      .limit(100)
      .onSnapshot(
        (snapshot) => setNotificationState((current) => ({
          ...current,
          recent: snapshot.docs.map((doc) => {
            const data: { read?: boolean; createdAt?: DateValue } = doc.data() || {};
            return {
              read: data.read === true,
              createdAt:
                dateValueToMillis(data.createdAt) || 0
            };
          })
        })),
        () => setNotificationState((current) => ({ ...current, recent: [] }))
      );
    return () => {
      unsubscribeOwner();
      unsubscribeRecent();
    };
  }, [firestore, notificationBadges, user?.uid]);

  const unreadNotifications = useMemo(
    () => notificationState.recent.filter((item) =>
      !item.read && item.createdAt > notificationState.lastViewedAt
    ).length,
    [notificationState]
  );

  const isEventsView = routeSection === "notifications" ||
    new URLSearchParams(location.search).get("tab") === "events";
  const visibleUnreadNotifications =
    notificationBadges && !isEventsView ? unreadNotifications : 0;
  const legendaryTravelTitle = legendaryAvailability.card && legendaryAvailability.dungeon
    ? t("На этой неделе доступны легендарная карта и экспедиция")
    : legendaryAvailability.card
      ? t("На этой неделе доступна легендарная карта")
      : legendaryAvailability.dungeon
        ? t("На этой неделе доступна легендарная экспедиция")
        : "";

  const tabs = [
    { key: "profile", label: t("Профиль"), icon: <IoIosPerson size={20} title={t("Профиль")} /> },
    { key: "results", label: t("Мои итоги"), icon: <FaChartPie size={18} title={t("Мои итоги")} /> },
    { key: "estimates", label: t("Статистика"), icon: <IoMdStarHalf size={20} title={t("Статистика")} /> },
    {
      key: "activity",
      label: t("Дневник"),
      icon: <FaBookOpen size={18} title={t("Дневник")} />,
      badge: visibleUnreadNotifications
    },
    {
      key: "collections",
      label: t("Что посмотреть"),
      icon: <IoMdAlbums size={20} title={t("Что посмотреть")} />,
      groupStart: true
    },
    { key: "films", label: t("Мои списки"), icon: <IoMdFilm size={20} title={t("Мои списки")} /> },
    {
      key: "friends",
      label: t("Друзья"),
      icon: <IoIosPeople size={20} title={t("Друзья")} />,
      groupStart: true
    },
    {
      key: "game",
      label: t("Путешествия"),
      icon: <FaCompass size={18} title={t("Путешествия")} />,
      legendary: legendaryHighlights && legendaryAvailability.any,
      legendaryTitle: legendaryTravelTitle,
      groupStart: true
    },
    { key: "achievements", label: t("Достижения"), icon: <IoMdTrophy size={20} title={t("Достижения")} /> }
  ];

  const togglePanelState = () => {
    setIsExpanded((prev) => !prev);
  };

  const handleTabClick = (_key?: string | React.MouseEvent<HTMLAnchorElement>) => {
    if (onCloseMobileMenu) {
      onCloseMobileMenu();
    }
  };

  const sourcesPath = userId ? `/${userId}/sources` : "/sources";

  return (
    <>
      {/* Mobile overlay */}
      {isMobileMenuOpen && (
        <div
          className="mobile-menu-overlay"
          onClick={onCloseMobileMenu}
        />
      )}

      <div
        className={`left-panel ${isMobileMenuOpen ? 'mobile-open' : ''} ${isExpanded ? "left-panel--expanded" : "left-panel--collapsed"}`}
        style={{ width: isExpanded && !isMobileMenuOpen ? "185px" : "45px" }}
      >
        <div className="tabs-block">
          {tabs.map(({ key, label, icon, badge, legendary, legendaryTitle, groupStart }) => (
            <NavLink
              key={key}
              onClick={() => handleTabClick(key)}
              onMouseEnter={() => preloadRoute(key)}
              onFocus={() => preloadRoute(key)}
              onTouchStart={() => preloadRoute(key)}
              className={({ isActive }) => [
                "tab",
                isActive || activeSection === key ? "active-tab" : "",
                legendary ? "tab--legendary-available" : "",
                groupStart ? "tab--group-start" : ""
              ].filter(Boolean).join(" ")}
              to={`/${userId}/${key}`}
              data-onboarding-target={key === "collections" ? "collections" : undefined}
              title={legendaryTitle || undefined}
              end
            >
              <span className="tab-icon-wrap">
                {icon}
                {(badge || 0) > 0 && <span className="tab-badge">{(badge || 0) > 99 ? "99+" : badge}</span>}
                {legendary && (
                  <span className="tab-legendary-marker" aria-hidden="true">
                    <FaCrown />
                  </span>
                )}
              </span>
              {(isExpanded || isMobileMenuOpen) && label}
            </NavLink>
          ))}
        </div>
        <div className="left-panel__bottom">
          {!isMobileMenuOpen && (
            <div className="collapse-block">
              {isExpanded ? (
                <IoIosArrowBack size={20} onClick={togglePanelState} title={t("Свернуть")} />
              ) : (
                <IoIosArrowForward size={20} onClick={togglePanelState} title={t("Развернуть")} />
              )}
            </div>
          )}
          <Link
            className="left-panel__sources-link"
            to={sourcesPath}
            onClick={handleTabClick}
            title={t("Источники данных TMDB")}
            aria-label={t("Источники данных TMDB")}
          >
            <img src={tmdbShortLogo} alt="TMDB" loading="lazy" />
            {(isExpanded || isMobileMenuOpen) && <span>{t("Источники данных")}</span>}
          </Link>
        </div>
      </div>
    </>
  );
}

export default LeftPanel;
